// COST-01: margin by trade report (costing.view). Colours use the workspace
// red line and each trade's green target (Settings → Team → Margin targets).
import { useMemo, useState } from 'react'
import './MarginReport.css'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/ui/PageHeader'
import { useApiData } from '@/hooks/useApi'
import { formatMoney } from '@/lib/format'
import { marginTone } from '@/components/JobCosting/JobProfitBar'
import { costingApi, designMode, type CostingSettings, type TradeMarginRow } from '@/components/JobCosting/api'

const TONE = { good: 'var(--mint-text)', warn: 'var(--butter-text)', bad: 'var(--blush-text)', none: 'var(--midnight-text3)' }
const RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
] as const
const cell = { padding: '8px 6px', fontSize: 13 }
const pct = (n: number | null) => (n == null ? '—' : `${n}%`)

export default function MarginReport() {
  return <MarginReportContent key={String(designMode())} />
}

function MarginReportContent() {
  const demo = designMode()
  const [range, setRange] = useState<string>('90')
  const from = useMemo(() => (range === 'all' ? undefined : new Date(Date.now() - Number(range) * 86_400_000).toISOString()), [range])
  const rows = useApiData<TradeMarginRow[]>(() => costingApi.byTrade(from ? { from } : undefined), [from])
  const settings = useApiData<CostingSettings>(() => costingApi.settings(), [])
  const redBelow = settings.data?.redBelow ?? 26
  const data = rows.data ?? []
  const total = data.reduce((a, r) => ({ p: a.p + r.price, c: a.c + r.actualCost, j: a.j + r.jobCount }), { p: 0, c: 0, j: 0 })
  const overall = total.p > 0 ? Math.round(((total.p - total.c) / total.p) * 10000) / 100 : null

  return (
    <main className="page-container animate-fade-up margin-report" aria-label="Margin by trade">
      {demo && <p>Demo — no workspace figures are shown.</p>}
      <PageHeader
        title="Margin by trade"
        back={{ to: '/reports', label: 'Reports' }}
        actions={
          <label className="margin-report-period">
            Period{' '}
            <select className="input-field" aria-label="Period" value={range} onChange={(e) => setRange(e.target.value)}>
              {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        }
      />
      <p className="text-sm" style={{ color: 'var(--midnight-text3)', marginBottom: 12 }}>
        Jobs with a costing estimate. Red below {redBelow}%; green at the trade's target. <Link to={demo ? '/settings/team?__design=job-costing' : '/settings/team'} style={{ display: 'inline-block', paddingBlock: 14, marginBlock: -14 }}>Edit targets</Link>
      </p>
      {rows.loading && !rows.data ? <div className="h-10 shimmer rounded" /> : rows.error ? (
        <p role="alert">{rows.error}</p>
      ) : data.length === 0 ? (
        <p className="text-sm">No costed jobs in this period yet.</p>
      ) : (
        <table className="margin-report-table ds-tile" aria-label="Margin by trade" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--midnight-text3)' }}>
              <th style={cell}>Trade</th><th style={cell}>Jobs</th><th style={cell}>Revenue</th><th style={cell}>Actual cost</th>
              <th style={cell}>Est. margin</th><th style={cell}>Margin</th><th style={cell}>Target</th>
            </tr>
          </thead>
          <tbody>
            {data.map((r) => {
              const tone = marginTone(r.marginPercent, { redBelow, greenAt: r.targetMarginPercent })
              return (
                <tr key={r.tradeId ?? 'none'} data-testid="trade-row" data-tone={tone}>
                  <td data-label="Trade" style={cell}>{r.tradeName}</td>
                  <td data-label="Jobs" style={cell}>{r.jobCount}</td>
                  <td data-label="Revenue" style={cell}>{formatMoney(r.price)}</td>
                  <td data-label="Actual cost" style={cell}>{formatMoney(r.actualCost)}</td>
                  <td data-label="Est. margin" style={cell}>{pct(r.estimatedMarginPercent)}</td>
                  <td data-label="Margin" style={{ ...cell, fontWeight: 600, color: TONE[tone] }}>{pct(r.marginPercent)}</td>
                  <td data-label="Target" style={cell}>{r.targetMarginPercent}%</td>
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 600 }}>
              <td style={cell}>All trades</td><td data-label="Jobs" style={cell}>{total.j}</td><td data-label="Revenue" style={cell}>{formatMoney(total.p)}</td>
              <td data-label="Actual cost" style={cell}>{formatMoney(total.c)}</td><td style={cell} /><td data-label="Margin" style={cell} data-testid="overall-margin">{pct(overall)}</td><td style={cell} />
            </tr>
          </tfoot>
        </table>
      )}
    </main>
  )
}
